import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Divider,
  Switch,
  FormControlLabel,
  Button,
  TextField,
  Grid,
  Alert,
  Snackbar,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Slider,
  Stack
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { useColorMode } from '../contexts/ColorModeContext';

const Settings: React.FC = () => {
  const { mode, toggleColorMode } = useColorMode();
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [emailAddress, setEmailAddress] = useState('admin@example.com');
  const [refreshInterval, setRefreshInterval] = useState(60);
  const [alertThreshold, setAlertThreshold] = useState(70);
  const [warningThreshold, setWarningThreshold] = useState(80);
  const [criticalThreshold, setCriticalThreshold] = useState(90);
  const [successMessage, setSuccessMessage] = useState('');
  
  const handleSaveSettings = () => {
    // Here you would typically save the settings to the backend
    setSuccessMessage('Settings saved successfully');
    
    // Automatically hide the success message after 3 seconds
    setTimeout(() => {
      setSuccessMessage('');
    }, 3000);
  };
  
  return (
    <Box>
      <Typography variant="h5" component="h1" gutterBottom>
        System Settings
      </Typography>
      
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity="success" variant="filled">
          {successMessage}
        </Alert>
      </Snackbar>
      
      <Grid container spacing={3}>
        {/* Appearance Settings */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 2 }}>
            <Typography variant="h6" gutterBottom>
              Appearance
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <FormControlLabel
              control={
                <Switch
                  checked={mode === 'dark'}
                  onChange={toggleColorMode}
                  color="primary"
                />
              }
              label="Dark Mode"
            />
          </Paper>
        </Grid>
        
        {/* Notification Settings */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 2 }}>
            <Typography variant="h6" gutterBottom>
              Notifications
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Stack spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationsEnabled}
                    onChange={(e) => setNotificationsEnabled(e.target.checked)}
                    color="primary"
                  />
                }
                label="Enable Notifications"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={emailNotifications}
                    onChange={(e) => setEmailNotifications(e.target.checked)}
                    color="primary"
                    disabled={!notificationsEnabled}
                  />
                }
                label="Email Notifications"
              />
              
              <TextField
                label="Email Address"
                variant="outlined"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                fullWidth
                disabled={!notificationsEnabled || !emailNotifications}
                size="small"
                margin="normal"
              />
            </Stack>
          </Paper>
        </Grid>
        
        {/* Monitoring Settings */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 2 }}>
            <Typography variant="h6" gutterBottom>
              Monitoring Settings
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <Typography gutterBottom>
                    Dashboard Refresh Interval (seconds)
                  </Typography>
                  <Slider
                    value={refreshInterval}
                    onChange={(_, newValue) => setRefreshInterval(newValue as number)}
                    aria-labelledby="refresh-interval-slider"
                    valueLabelDisplay="auto"
                    step={10}
                    marks
                    min={10}
                    max={300}
                  />
                  <Box sx={{ textAlign: 'center', mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {refreshInterval} seconds
                    </Typography>
                  </Box>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="default-view-label">Default Dashboard View</InputLabel>
                  <Select
                    labelId="default-view-label"
                    id="default-view"
                    value="dashboard"
                    label="Default Dashboard View"
                  >
                    <MenuItem value="dashboard">Main Dashboard</MenuItem>
                    <MenuItem value="system-health">System Health</MenuItem>
                    <MenuItem value="model-performance">Model Performance</MenuItem>
                    <MenuItem value="api-monitoring">API Monitoring</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <FormControl fullWidth margin="normal">
                  <Typography gutterBottom>
                    Alert Threshold (%)
                  </Typography>
                  <Slider
                    value={alertThreshold}
                    onChange={(_, newValue) => setAlertThreshold(newValue as number)}
                    aria-labelledby="alert-threshold-slider"
                    valueLabelDisplay="auto"
                    step={5}
                    marks
                    min={50}
                    max={95}
                  />
                  <Box sx={{ textAlign: 'center', mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {alertThreshold}%
                    </Typography>
                  </Box>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <FormControl fullWidth margin="normal">
                  <Typography gutterBottom>
                    Warning Threshold (%)
                  </Typography>
                  <Slider
                    value={warningThreshold}
                    onChange={(_, newValue) => setWarningThreshold(newValue as number)}
                    aria-labelledby="warning-threshold-slider"
                    valueLabelDisplay="auto"
                    step={5}
                    marks
                    min={50}
                    max={95}
                  />
                  <Box sx={{ textAlign: 'center', mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {warningThreshold}%
                    </Typography>
                  </Box>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <FormControl fullWidth margin="normal">
                  <Typography gutterBottom>
                    Critical Threshold (%)
                  </Typography>
                  <Slider
                    value={criticalThreshold}
                    onChange={(_, newValue) => setCriticalThreshold(newValue as number)}
                    aria-labelledby="critical-threshold-slider"
                    valueLabelDisplay="auto"
                    step={5}
                    marks
                    min={50}
                    max={95}
                  />
                  <Box sx={{ textAlign: 'center', mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {criticalThreshold}%
                    </Typography>
                  </Box>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        {/* API Connection Settings */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 2 }}>
            <Typography variant="h6" gutterBottom>
              API Connection Settings
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="API Base URL"
                  variant="outlined"
                  defaultValue="http://localhost:8000"
                  fullWidth
                  size="small"
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  label="WebSocket URL"
                  variant="outlined"
                  defaultValue="ws://localhost:8765"
                  fullWidth
                  size="small"
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  label="API Key"
                  variant="outlined"
                  type="password"
                  defaultValue="api-key-placeholder"
                  fullWidth
                  size="small"
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="auth-method-label">Authentication Method</InputLabel>
                  <Select
                    labelId="auth-method-label"
                    id="auth-method"
                    defaultValue="jwt"
                    label="Authentication Method"
                    size="small"
                  >
                    <MenuItem value="jwt">JWT</MenuItem>
                    <MenuItem value="api-key">API Key</MenuItem>
                    <MenuItem value="oauth">OAuth 2.0</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        {/* Save Button */}
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<SaveIcon />}
            onClick={handleSaveSettings}
            size="large"
          >
            Save Settings
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Settings;
